/**
 * The settlement queue — status tabs, the balance preflight, and cursor paging.
 *
 * **Why a status tab and not "all".** The api's ORDER BY (`-created_at, -id`)
 * only reaches `idx_xp_status_created` when status pins its leading column; an
 * unfiltered queue filesorts the whole table. `pending` is both the cheapest
 * query and the operator's actual working set, so it is the default.
 *
 * **There is no date-range filter, and that is deliberate.** `expertPayments`
 * takes no date argument of any kind. Adding one client-side would only filter
 * the page already fetched, which is worse than not having it. The actionable
 * statuses are a small working set that ages toward the top of a newest-first
 * queue, and the genuine date question — the tax year — is what the ledger
 * answers. A monthly-run window belongs with bulk settlement (ENG-5625).
 *
 * **Paging drifts, honestly.** Cursors are offset-based, so a row inserted while
 * an operator pages shifts the window under them. Nothing client-side can fix
 * that; what this does instead is re-read from page one after every settlement,
 * so the queue an operator acts on is never a stale accumulation.
 *
 * **That re-read is exposed as a ref, not driven by a changing `key`.** The
 * console has to trigger it too, after a manual record lands a row in `paid`. A
 * remount would get the fresh read and silently throw away three things this
 * component holds on purpose: the `executionFrozen` latch, the open status tab,
 * and every page already pulled in with `fetchMore`.
 */
import { useQuery } from '@apollo/client';
import { forwardRef, useImperativeHandle, useState } from 'react';

import {
  ExpertPaymentKind,
  ExpertPaymentSourceType,
  ExpertPaymentStatus,
} from '@/__generated__/graphql';
import Button from '@/componentsv2/Button';
import Input from '@/componentsv2/Input';

import EnumField from '../EnumField';
import { describePagingError } from '../errors';
import { formatMoney } from '../format';
import { EXPERT_PAYMENTS_QUEUE, STRIPE_BALANCE } from '../operations';
import { KIND_OPTIONS, SOURCE_TYPE_OPTIONS, STATUS_TABS } from '../vocabulary';
import PaymentTable from './PaymentTable';

const PAGE_SIZE = 25;

/** The "no facet" sentinel for the two enum filters. `<select>` has no null. */
const ANY = '';

/**
 * Why the list is empty — which is a different sentence depending on whether a
 * facet is what emptied it. Extracted rather than nested inline so the copy is
 * one expression and not three conditional text nodes.
 */
function emptyMessage(facetsActive: boolean, status: ExpertPaymentStatus): string {
  if (facetsActive) {
    return 'Nothing matches these filters. Widen them to see the rest of this tab.';
  }
  return status === ExpertPaymentStatus.Pending
    ? 'Nothing here. No expert is waiting to be paid right now.'
    : 'Nothing here.';
}

export interface PaymentQueueHandle {
  /** Re-read from page one, keeping the tab, the latch and the mount. */
  reload: () => void;
}

const PaymentQueue = forwardRef<PaymentQueueHandle>(function PaymentQueue(_, ref): JSX.Element {
  const [status, setStatus] = useState<ExpertPaymentStatus>(ExpertPaymentStatus.Pending);
  const [sourceType, setSourceType] = useState<ExpertPaymentSourceType | ''>(ANY);
  const [kind, setKind] = useState<ExpertPaymentKind | ''>(ANY);
  // Committed separately from what is being typed: a payee id is only meaningful
  // once it is whole, and re-querying on every keystroke would page the queue
  // against "8", "82", "827" on the way to "8277".
  const [payeeInput, setPayeeInput] = useState('');
  const [payee, setPayee] = useState('');
  const [reachedEnd, setReachedEnd] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  // Latched for the session: once one Pay reports the freeze, every Pay button
  // afterwards explains itself instead of each one discovering it by failing.
  // The kill switch is not exposed over GraphQL, so attempting is the only way
  // to learn about it.
  const [executionFrozen, setExecutionFrozen] = useState(false);

  const { data, loading, error, refetch, fetchMore } = useQuery(EXPERT_PAYMENTS_QUEUE, {
    // Unset facets are omitted rather than sent as null: they are part of
    // Apollo's cache key, so a `sourceType: null` would split the cache from the
    // plain status read and make every mock and story carry an unused variable.
    variables: {
      status,
      first: PAGE_SIZE,
      ...(sourceType ? { sourceType } : {}),
      ...(kind ? { kind } : {}),
      ...(payee ? { payee } : {}),
    },
    // A settled row leaves its tab, so a cached queue is only good for the first
    // paint. Revalidation resets to page one by design — see the module note.
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const page = data?.expertPayments;
  const edges = page?.edges ?? [];
  const payments = edges.map((e) => e.node);
  const total = page?.pageInfo.total ?? null;

  /**
   * Narrowing the queue restarts it.
   *
   * Apollo re-executes from page one on its own once the variables change — the
   * cursor lives only in `fetchMore`, never in the hook's variables. What it
   * cannot know about is the local paging state, so every filter has to clear it
   * here or a narrowed queue inherits the previous one's "that's everything".
   */
  function applyFilter(change: () => void) {
    change();
    setReachedEnd(false);
    setLoadMoreError(null);
  }

  function reload() {
    setReachedEnd(false);
    setLoadMoreError(null);
    void refetch();
  }

  useImperativeHandle(ref, () => ({ reload }));

  async function loadMore() {
    // PageInfo carries no endCursor — the next cursor is the last edge's.
    const cursor = edges.at(-1)?.cursor;
    if (!cursor) return;
    setLoadingMore(true);
    setLoadMoreError(null);
    try {
      const res = await fetchMore({
        variables: { after: cursor },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.expertPayments) return prev;
          return {
            ...prev,
            expertPayments: {
              ...fetchMoreResult.expertPayments,
              edges: [...prev.expertPayments.edges, ...fetchMoreResult.expertPayments.edges],
            },
          };
        },
      });
      if (!res.data?.expertPayments?.pageInfo.hasNextPage) setReachedEnd(true);
    } catch (err) {
      // Keep the button alive rather than failing silently — most causes are
      // recoverable by retrying, and hiding the control strands the operator
      // mid-queue. A rejected cursor is the exception, and says so.
      setLoadMoreError(describePagingError(err));
    } finally {
      setLoadingMore(false);
    }
  }

  const hasMore = !reachedEnd && (page?.pageInfo.hasNextPage ?? false);
  const facetsActive = Boolean(sourceType || kind || payee);

  return (
    <section className="flex flex-col gap-24">
      <BalancePreflight />

      <nav aria-label="Payment status" className="flex flex-wrap gap-8">
        {/* Branched rather than a computed `variant`: the prop is a
            discriminated union (tertiary carries `invert`, the others do not),
            so a `'primary' | 'tertiary'` union does not narrow. */}
        {STATUS_TABS.map((tab) =>
          tab.value === status ? (
            <Button
              key={tab.value}
              size="small"
              aria-current="page"
              onClick={() => applyFilter(() => setStatus(tab.value))}
            >
              {tab.label}
            </Button>
          ) : (
            <Button
              key={tab.value}
              size="small"
              variant="tertiary"
              onClick={() => applyFilter(() => setStatus(tab.value))}
            >
              {tab.label}
            </Button>
          )
        )}
      </nav>

      <Facets
        sourceType={sourceType}
        kind={kind}
        payeeInput={payeeInput}
        onSourceType={(v) => applyFilter(() => setSourceType(v))}
        onKind={(v) => applyFilter(() => setKind(v))}
        onPayeeInput={setPayeeInput}
        onCommitPayee={() => applyFilter(() => setPayee(payeeInput.trim()))}
        onClear={() =>
          applyFilter(() => {
            setSourceType(ANY);
            setKind(ANY);
            setPayeeInput('');
            setPayee('');
          })
        }
      />

      <header className="flex flex-wrap items-baseline justify-between gap-8">
        <h2 className="text-on-surface hd-5">
          {STATUS_TABS.find((t) => t.value === status)?.label ?? 'Payments'}
        </h2>
        {/* Only the open tab's count. Badging every tab would mean five
            full-table counts on load, and a count that goes stale the moment
            someone settles a row is worse than no count. */}
        {total !== null ? (
          <span className="text-on-surface-muted body-2">
            {`${total} ${total === 1 ? 'payment' : 'payments'}`}
          </span>
        ) : null}
      </header>

      {error && payments.length === 0 ? (
        <div className="rounded-lg border border-surface-border bg-surface-card p-24">
          <p className="text-on-surface subtitle-1">We couldn’t load the payment queue.</p>
          <p className="mt-4 text-on-surface-muted body-2">
            Nothing has been changed. Try again in a moment.
          </p>
          <div className="mt-16">
            <Button onClick={reload}>Retry</Button>
          </div>
        </div>
      ) : null}

      {!error && payments.length === 0 && !loading ? (
        <p className="rounded-lg border border-surface-border bg-surface-card p-24 text-on-surface-muted body-2">
          {/* "No expert is waiting to be paid" is a claim about the whole
              platform, so it can only be made when nothing is narrowing the
              queue. With a facet on, the honest statement is about the filter. */}
          <span>{emptyMessage(facetsActive, status)}</span>
        </p>
      ) : null}

      {payments.length > 0 ? (
        <PaymentTable
          payments={payments}
          onSettled={reload}
          executionFrozen={executionFrozen}
          onExecutionFrozen={() => setExecutionFrozen(true)}
        />
      ) : null}

      {hasMore ? (
        <div className="flex flex-col gap-8">
          <Button
            variant="secondary"
            onClick={() => void loadMore()}
            loading={loadingMore}
            disabled={loadingMore}
          >
            Load more
          </Button>
          {loadMoreError !== null ? (
            <p role="alert" className="text-red-900 body-2 dark:text-red-200">
              {loadMoreError}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
});

export default PaymentQueue;

/**
 * The non-status facets: source type, kind and payee.
 *
 * Status stays a row of tabs above this — it is the operator's working set and
 * has to be one click, whereas these three narrow within it. All four are server
 * arguments; none of this filters a page already fetched.
 *
 * Payee is a raw user id and is labelled as one. There is no batch user-search
 * query in the schema, so a name box here would either lie about what it matches
 * or fan out a lookup per keystroke; the queue and the ledger both show the name
 * beside the id, which is where an operator gets the id to paste.
 *
 * Committed on submit rather than on change, so the queue is not re-paged
 * against every prefix on the way to a whole id. A `<form>` gets Enter for free.
 */
function Facets({
  sourceType,
  kind,
  payeeInput,
  onSourceType,
  onKind,
  onPayeeInput,
  onCommitPayee,
  onClear,
}: {
  sourceType: ExpertPaymentSourceType | '';
  kind: ExpertPaymentKind | '';
  payeeInput: string;
  onSourceType: (value: ExpertPaymentSourceType | '') => void;
  onKind: (value: ExpertPaymentKind | '') => void;
  onPayeeInput: (value: string) => void;
  onCommitPayee: () => void;
  onClear: () => void;
}): JSX.Element {
  const active = Boolean(sourceType || kind || payeeInput);

  return (
    <form
      aria-label="Filter payments"
      className="flex flex-wrap items-end gap-16"
      onSubmit={(e) => {
        e.preventDefault();
        onCommitPayee();
      }}
    >
      <EnumField
        id="xp-facet-source-type"
        label="Paid for"
        value={sourceType}
        options={[{ value: ANY, label: 'Anything' }, ...SOURCE_TYPE_OPTIONS]}
        onChange={(v) => onSourceType(v as ExpertPaymentSourceType | '')}
      />
      <EnumField
        id="xp-facet-kind"
        label="Kind"
        value={kind}
        options={[{ value: ANY, label: 'Any kind' }, ...KIND_OPTIONS]}
        onChange={(v) => onKind(v as ExpertPaymentKind | '')}
      />
      {/* "Expert id", not "Expert user id": the manual-record form has a field
          by that exact name meaning something different — the payee a new row is
          being created for — and two identically labelled inputs on one screen
          is ambiguous for an operator and for a screen reader.

          Width-capped because `Input` is a full-width MUI TextField: unconstrained
          it takes the whole row and pushes Apply onto a line of its own. An id is
          a handful of digits, so the field should not be wider than one. */}
      <div className="w-[180px]">
        <Input
          id="xp-facet-payee"
          label="Expert id"
          value={payeeInput}
          onChange={(e) => onPayeeInput(e.target.value)}
          placeholder="e.g. 8277"
        />
      </div>
      <Button type="submit" variant="secondary" size="small">
        Apply
      </Button>
      {active ? (
        <Button type="button" variant="tertiary" size="small" onClick={onClear}>
          Clear filters
        </Button>
      ) : null}
    </form>
  );
}

/**
 * What Stripe is holding, before anyone releases anything.
 *
 * Per currency, never summed across — two entries are two separate answers to
 * "can we cover this run", and adding EUR minor units to USD ones would produce
 * a number that means nothing. `available` is what can actually move today;
 * `pending` has not settled yet and cannot.
 *
 * A failed balance read is shown as unavailable rather than as an error state
 * that blocks the queue: the queue is still workable without it, and the api
 * deliberately surfaces the failure rather than a silent zero.
 */
function BalancePreflight(): JSX.Element {
  const { data, error, loading } = useQuery(STRIPE_BALANCE, { fetchPolicy: 'cache-and-network' });

  const available = data?.stripeBalance.available ?? [];
  const pending = data?.stripeBalance.pending ?? [];

  return (
    <div className="rounded-lg border border-surface-border bg-surface-card p-16">
      <h2 className="text-on-surface-muted subtitle-2">Platform Stripe balance</h2>
      {error ? (
        <p className="mt-8 text-on-surface-muted body-2">
          Couldn’t read the Stripe balance. The queue below is unaffected — but check the balance in
          Stripe before releasing a large run.
        </p>
      ) : loading && available.length === 0 ? (
        <p className="mt-8 text-on-surface-muted body-2">Reading…</p>
      ) : (
        <div className="mt-8 flex flex-wrap gap-24">
          <Balances label="Available now" amounts={available} />
          <Balances label="Pending settlement" amounts={pending} />
        </div>
      )}
    </div>
  );
}

function Balances({
  label,
  amounts,
}: {
  label: string;
  amounts: { cents: string; currency: string }[];
}): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-on-surface-muted util-caption">{label}</span>
      {amounts.length === 0 ? (
        <span className="text-on-surface body-1">—</span>
      ) : (
        amounts.map((a) => (
          <span key={a.currency} className="text-on-surface body-1">
            {formatMoney(a)}
          </span>
        ))
      )}
    </div>
  );
}
